import { spawn } from "node:child_process";
import { LocalCliError } from "./errors.js";

/** Flags whose following value(s) are secrets and must never reach an error message. */
const SECRET_VALUE_FLAGS = new Set(["--secure-environment-variables"]);
/** KEY= tokens whose KEY name looks secret-bearing — redact the value, keep the key. */
const SECRET_KEY_RE = /(pem|secret|token|password|passwd|pwd|credential|private[-_]?key)/i;

/**
 * Return a copy of `az` args safe to embed in an error message. The value of any
 * secret-bearing flag (notably `--secure-environment-variables GH_APP_PEM_B64=…`,
 * which carries the GitHub App RSA private key) and any `KEY=VALUE` whose key
 * looks like a secret is replaced with `[REDACTED]`. Without this, a failed
 * `az container create` leaked the App private key verbatim into AZ_COMMAND_FAILED.
 */
export function redactAzArgs(args: string[]): string[] {
  const out: string[] = [];
  let secretFlagActive = false;
  for (const arg of args) {
    if (arg.startsWith("-")) {
      // A new flag ends any variadic secret-value run and starts a fresh check.
      secretFlagActive = SECRET_VALUE_FLAGS.has(arg);
      out.push(arg);
      continue;
    }
    const eq = arg.indexOf("=");
    const key = eq > 0 ? arg.slice(0, eq) : "";
    if (secretFlagActive) {
      // Every following non-flag token is a value of the secret flag (variadic).
      out.push(key ? `${key}=[REDACTED]` : "[REDACTED]");
      continue;
    }
    if (key && SECRET_KEY_RE.test(key)) {
      out.push(`${key}=[REDACTED]`);
      continue;
    }
    out.push(arg);
  }
  return out;
}

/** Run an `az` CLI command, resolving stdout. Throws LocalCliError on failure. */
export function runAz(args: string[]): Promise<string> {
  return new Promise((resolve, reject) => {
    const proc = spawn("az", args, { stdio: ["ignore", "pipe", "pipe"] });
    const out: Buffer[] = [];
    const err: Buffer[] = [];
    proc.stdout.on("data", (d: Buffer) => out.push(d));
    proc.stderr.on("data", (d: Buffer) => err.push(d));
    proc.on("error", (e) => {
      if ((e as NodeJS.ErrnoException).code === "ENOENT") {
        reject(
          new LocalCliError({
            code: "AZ_NOT_INSTALLED",
            message: "The 'az' command is not installed or not on PATH.",
            hint: "Install Azure CLI: https://learn.microsoft.com/cli/azure/install-azure-cli",
          }),
        );
        return;
      }
      reject(e);
    });
    proc.on("close", (code) => {
      const errString = Buffer.concat(err).toString("utf8");
      if (code !== 0) {
        if (/please run.*az login/i.test(errString) || /not logged in/i.test(errString)) {
          reject(
            new LocalCliError({
              code: "AUTH_NOT_SIGNED_IN",
              message: "You are not signed in to Azure.",
              hint: "Run 'az login' to sign in.",
            }),
          );
          return;
        }
        reject(
          new LocalCliError({
            code: "AZ_COMMAND_FAILED",
            message: `'az ${redactAzArgs(args).join(" ")}' failed: ${errString.trim()}`,
          }),
        );
        return;
      }
      resolve(Buffer.concat(out).toString("utf8"));
    });
  });
}

/**
 * Rethrow only az-environment failures (not signed in / not installed) — those
 * must surface clearly. Every other failure (notably a not-found resource, which
 * `runAz` reports as AZ_COMMAND_FAILED) is left for the caller to treat as
 * "absent", with an idempotent create as the backstop.
 */
export function rethrowIfAzUnavailable(e: unknown): void {
  if (e instanceof LocalCliError && (e.code === "AUTH_NOT_SIGNED_IN" || e.code === "AZ_NOT_INSTALLED")) {
    throw e;
  }
}
